import type { IntlShape } from 'react-intl';

import type { MinMax } from '../../../../core/types/min-max.js';
import type { SeriesActionsTemplate } from '../../../../core/types/series-actions-template.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import type { SeriesIdToColorScale } from '../../../context/XYChartColorScales.context.js';
import type { FormatCategory } from '../../../hooks/useFormatCategory.js';
import { isCategorical } from '../../../types/guards.js';
import type { TooltipContent } from '../../../types/state.js';
import {
  BAR_VARIANT,
  RECT_VARIANT,
  type AxisIdToFormatter,
  type AxisIdToScaleDomain,
  type BarMetadata,
  type BarSeriesToMetadata,
  type BarSeriesWithDatapoints,
  type BaseMetadata,
  type DotMetadata,
  type LineMetadata,
  type MapDatapoints,
  type RectMetadata,
  type RectSeriesToMetadata,
  type SeriesToMetadata,
} from '../../../types/xy-chart-internals.js';
import { buildBarSeriesActionItem } from '../../../utils/build-series-action-item.js';
import { getRelativeAccessor } from '../../../utils/value-representation.js';
import { buildClosestPointTooltipBodyProps } from '../ClosestPointTooltip/closestPointTooltipBodyUtils.js';
import { getHoveredPointTooltipBodyContent } from '../HoveredPointTooltip/hooks/getHoveredPointTooltipBodyContent.js';
import { buildHoveredPointTooltipBodyProps } from '../HoveredPointTooltip/hoveredPointTooltipBodyUtils.js';
import { getColor } from '../utils/get-color.js';
import { getTooltipFormatter } from '../utils/get-tooltip-formatter.js';
import type { XYChartTooltipBodyProps } from '../XYChartTooltipBody/XYChartTooltipBody.js';

type SharedTooltipContext = {
  seriesToMetadata: SeriesToMetadata;
  axisIdToFormatter: AxisIdToFormatter;
  axisIdToScaleDomain: AxisIdToScaleDomain;
  datapointMetadataMap: MapDatapoints;
  formatCategory: FormatCategory;
  intl: IntlShape;
  allBarSeries: BarSeriesWithDatapoints[];
};

export type SharedTooltipRow = {
  name: string;
  color: string;
  yAxisValue: string;
  yAxisDuration: string | undefined;
  actions?: () => SeriesActionsTemplate;
};

export type SharedTooltipHeader = {
  xAxisValue: string;
  xAxisDuration: string | undefined;
  label: string | undefined;
};

/**
 * Bar/Rect have explicit hovered geometry, so they format via the
 * hovered-point helper. Dot/Line/Area pick the closest sample under
 * the cursor and format via the closest-point helper. Both helpers
 * return the same `XYChartTooltipBodyProps` shape.
 */
function buildTooltipBody(
  content: TooltipContent,
  ctx: SharedTooltipContext,
): XYChartTooltipBodyProps {
  const isHoveredShape =
    content.type === RECT_VARIANT || content.type === BAR_VARIANT;

  if (isHoveredShape) {
    return buildHoveredPointTooltipBodyProps({
      datapoint: content.datapoint,
      series: content.series,
      seriesToMetadata: ctx.seriesToMetadata as unknown as RectSeriesToMetadata,
      axisIdToFormatter: ctx.axisIdToFormatter,
      axisIdToScaleDomain: ctx.axisIdToScaleDomain,
      datapointMetadataMap: ctx.datapointMetadataMap,
      formatCategory: ctx.formatCategory,
      intl: ctx.intl,
    });
  }

  return buildClosestPointTooltipBodyProps({
    datapoint: content.datapoint,
    series: content.series,
    seriesToMetadata: ctx.seriesToMetadata as never,
    axisIdToFormatter: ctx.axisIdToFormatter,
    axisIdToScaleDomain: ctx.axisIdToScaleDomain,
    datapointMetadataMap: ctx.datapointMetadataMap,
    intl: ctx.intl,
  });
}

export function buildSharedTooltipHeader(
  preferred: TooltipContent,
  ctx: SharedTooltipContext,
): SharedTooltipHeader {
  const { xAxisValue, xAxisDuration } = buildTooltipBody(preferred, ctx);
  return {
    xAxisValue,
    xAxisDuration,
    label: preferred.datapoint.x0 instanceof Date ? '' : undefined,
  };
}

// Computes only the y-axis formatted value for a BAR/RECT row, reusing the
// already-retrieved metadata to avoid a second seriesToMetadata lookup and
// skipping x-axis formatting that is unused in shared tooltip rows.
function buildRowYAxisValue(
  result: Extract<
    TooltipContent,
    { type: typeof BAR_VARIANT | typeof RECT_VARIANT }
  >,
  metadata: BaseMetadata,
  ctx: SharedTooltipContext,
): { yAxisValue: string; yAxisDuration: string | undefined } {
  const {
    axisIdToFormatter,
    axisIdToScaleDomain,
    datapointMetadataMap,
    formatCategory,
    intl,
  } = ctx;
  const { yAxis } = metadata;

  const yResolvedFormatter = isCategorical(yAxis.type)
    ? (value: string) => formatCategory(yAxis, value) ?? value
    : getTooltipFormatter(yAxis, axisIdToFormatter, intl);

  const datapointMetadata = datapointMetadataMap.get(result.datapoint);
  const binDomain = datapointMetadata?.binDomain;
  const isRelative = typeof binDomain === 'number' && binDomain;

  // Mirrors the `xDomain` variable in buildHoveredPointTooltipBodyProps —
  // confusingly named there, it is the domain fed into the y-axis relative accessor.
  const yRelativeDomain: MinMax =
    isRelative && datapointMetadata?.baseline === 'x'
      ? { min: binDomain * -1, max: binDomain }
      : (safeGet(axisIdToScaleDomain, yAxis.id) as MinMax);

  const { y0, y1 } = result.datapoint;
  const { text: yAxisValue, duration: yAxisDuration } =
    getHoveredPointTooltipBodyContent(
      intl,
      y0,
      y1,
      yResolvedFormatter,
      getRelativeAccessor(yAxis, yRelativeDomain),
    );

  return { yAxisValue, yAxisDuration };
}

export function buildSharedTooltipRow(
  result: TooltipContent,
  ctx: SharedTooltipContext & { colorScaleMap: SeriesIdToColorScale },
): SharedTooltipRow {
  const {
    seriesToMetadata,
    datapointMetadataMap,
    colorScaleMap,
    allBarSeries,
  } = ctx;
  const metadata = safeGet(seriesToMetadata, result.series);
  const colorOverride =
    result.type === RECT_VARIANT
      ? safeGet(datapointMetadataMap, result.datapoint).colorOverride
      : undefined;

  // `getColor` returns the rect color from the color scale when applicable
  // and otherwise falls back to `metadata.color.Default`. The cast is safe
  // at runtime: `AreaMetadata` is structurally compatible.
  const color =
    getColor(
      result.series,
      result.datapoint,
      metadata as RectMetadata | BarMetadata | DotMetadata | LineMetadata,
      colorScaleMap,
      colorOverride,
    ) ?? '';

  // BAR/RECT: use the already-retrieved metadata and skip x-axis formatting,
  // which is unused in rows. Other shapes fall back to buildTooltipBody
  // (unreachable in the shared tooltip variant but kept for safety).
  const { yAxisValue, yAxisDuration } =
    result.type === BAR_VARIANT || result.type === RECT_VARIANT
      ? buildRowYAxisValue(result, metadata, ctx)
      : buildTooltipBody(result, ctx);

  let actions: (() => SeriesActionsTemplate) | undefined;
  if (result.type === BAR_VARIANT) {
    const metadataActions = (metadata as BarMetadata).actions;
    if (metadataActions) {
      const { datapoint, series } = result;
      actions = () => {
        const actionItem = buildBarSeriesActionItem({
          datapoint,
          allSeries: allBarSeries,
          seriesToMetadata: seriesToMetadata as unknown as BarSeriesToMetadata,
          datapointMetadataMap,
          currentSeries: series,
        });
        return metadataActions(actionItem);
      };
    }
  }

  return { name: metadata.name, color, yAxisValue, yAxisDuration, actions };
}
